import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '@services/notification/notification.service';
import {SortingService} from '@services/administration/sorting/sorting.service';

import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';

import {Machine} from '@models/machine/machine';
import {Organization} from '@models/organization/organization';
import {MachineCopySettings} from '@models/machine/machine-copy-settings';
import {ImportSettings} from '@models/machine/import-settings';
import {MachineCategoryService} from '@services/administration/portal-setup/machine-category/machine-category.service';
import {MachineCategory} from '@models/adminstration/portal-setup/machine-category/machine-category';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {USER_ROLE} from '@constants/user-role.constant';
import {ElasticSearchResponseEntity} from '@models/elastic-search/response/elastic-search-response-entity';
import {ElasticSearchResult} from '@models/elastic-search/response/elastic-search-result';
import {ElasticSearchService} from '@services/elastic-search/elastic-search.service';
import {User} from '@models/user/user';

@Component({
    selector: 'scitis-admin-import-settings',
    templateUrl: './import-settings.component.html',
    styleUrls: ['./import-settings.component.scss'],
    standalone: false
})
export class ImportSettingsComponent implements OnInit, OnDestroy {

  @Input()
  machine: Machine = new Machine();

  @Input()
  machines: Machine[] = new Array<Machine>();

  @Input()
  selectedMachineType = '';
  @Input()
  loggedUser: User = new User();

  isLoadingCategories: boolean = false;
  isLoadingOrganizations: boolean = false;
  displayGroup: boolean = false;
  selectedCategory: string = '';
  possibleCategories: string[] = new Array<string>();
  selectedParent: Organization = new Organization;
  possibleOrganizations: Organization[] = new Array<Organization>();
  importSettingsForm: UntypedFormGroup = new UntypedFormGroup({});
  copySettings: MachineCopySettings = new MachineCopySettings();
  submitted: boolean = false;
  unfilteredMachines: Machine[] = new Array<Machine>();

  private COMPONENT_NAME: string = 'ImportsettingsComponent';
  private destroy: Subject<boolean> = new Subject<boolean>();

  constructor(
      private activeModal: NgbActiveModal,
      private translateService: TranslateService,
      private notificationService: NotificationService,
      private machineCategoryService: MachineCategoryService,
      private sortingService: SortingService,
      private elasticSearchService: ElasticSearchService,
  ) {
    this.initializeCopySettings();
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.importSettingsForm.controls;
  }

  get isDisabledSaveBtn(): boolean {
    return this.submitted ? this.importSettingsForm.invalid : this.submitted && this.importSettingsForm.invalid;
  };

  ngOnInit(): void {
    this.machines.sort((a, b) => this.sortingService.sortByName(a, b));
    this.unfilteredMachines = this.machines;
    this.setPossibleOrganizations();
    this.setPossibleCategories();
    this.generateImportSettingsForm();
    this.displayGroup = !this.machine.id;
  }

  onCategoryChanged(category: string) {
    this.selectedCategory = category;
    if (!category || category.length === 0) {
      this.machines = this.unfilteredMachines;
    } else {
      this.machines = this.unfilteredMachines.filter(m => m.category === category);
    }
    if (this.selectedParent && this.selectedParent.id !== -1) {
      this.machines = this.machines.filter(m => m.parentId === this.selectedParent.id);
    }
    this.onResetMachine();
  }

  onOrganizationChanged(organization: Organization) {
    this.selectedParent = organization;
    if (organization) {
      this.machines = this.unfilteredMachines.filter(m => m.parentId === organization.id);
    } else {
      this.machines = this.unfilteredMachines;
    }
    if (this.selectedCategory && this.selectedCategory.length > 0) {
      this.machines = this.machines.filter(m => m.category === this.selectedCategory);
    }
    this.onResetMachine();
  }

  onClose(): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onCancel(): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  onSave(): void {
    this.submitted = true;
    if (this.importSettingsForm.invalid) {
      return;
    }
    const importSettings = new ImportSettings();
    importSettings.category = this.importSettingsForm.controls.category.value;
    importSettings.organization = this.importSettingsForm.controls.parentId.value;
    importSettings.machineType = this.importSettingsForm.controls.machineType.value;
    importSettings.copySettings = this.copySettings;
    this.activeModal.close(importSettings);
  }

  ngOnDestroy() {
    this.destroy.next(true);
    this.destroy.complete();
  }

  private generateImportSettingsForm(): void {
    this.importSettingsForm = new UntypedFormGroup({
      category: new UntypedFormControl(this.machine.category),
      parentId: new UntypedFormControl(this.machine.parentId),
      machineType: new UntypedFormControl(this.selectedMachineType)
    });
  }

  private setPossibleCategories(): void {
    this.isLoadingCategories = true;
    this.machineCategoryService.listMachineCategories().pipe(takeUntil(this.destroy)).subscribe({
      next: (categories: MachineCategory[]) => {
        for (const category of categories) {
          this.possibleCategories.push(category.name);
        }

        this.isLoadingCategories = false;
      }, error: error => {
        const message = this.translateService.instant('Error getting categories list.');
        this.notificationService.error(message, error, this.COMPONENT_NAME, true);
        this.isLoadingCategories = false;
      }
    });
  }

  private setPossibleOrganizations(): void {
    this.isLoadingOrganizations = true;
    const rootOrganizationId = this.loggedUser.role === USER_ROLE.SUPERADMIN ? -1 : this.loggedUser.parentId;
    this.elasticSearchService.listAllOrganizations(rootOrganizationId).pipe(takeUntil(this.destroy)).subscribe({
      next: (success: ElasticSearchResponseEntity) => {
        if (success?.result?.data && success.result.data.length > 0) {
          const result: ElasticSearchResult = success.result;
          this.possibleOrganizations = [...result.data].sort((a, b) => this.sortingService.sortByName(a, b));
        }

        this.isLoadingOrganizations = false;
      }, error: error => {
        const message = this.translateService.instant('Error getting organizations list.');
        this.notificationService.error(message, error, this.COMPONENT_NAME, true);
        this.isLoadingOrganizations = false;
      }
    });

  }

  private initializeCopySettings() {
    this.copySettings.copyDataConfiguration = true;
    this.copySettings.copyExternalUrlConfiguration = true;
    this.copySettings.copyTileConfiguration = true;
    this.copySettings.copyTrafficLightConfiguration = true;
    this.copySettings.copyTableauConfiguration = true;
    this.copySettings.copyStatusIndicator = true;
    this.copySettings.copyInlineFrameCockpitConfiguration = true;
  }

  private onResetMachine() {
    this.machine = new Machine();
    this.importSettingsForm.controls.machineType.setValue('');
  }
}
